import { PLAN_HISTORY_FIELDS, type PlanChanges } from '@/lib/billing/plan-changes'
import { BILLING_CHANNELS } from '@/lib/billing/constants'
import type { NumericField, SectionId } from './catalog'
import { englishPlanPresentation, formatPlanUnit, type PlanPresentation } from './presentation'
import { DEFAULT_PLAN_FORM } from './defaults'
import { fieldSection, toEditorAmount } from './model'
import type { PlanPayload } from './types'

export interface PlanChangeRow {
  field: string
  label: string
  section: SectionId
  from: string
  to: string
}

export interface PlanValueRow {
  field: string
  label: string
  section: SectionId
  value: string
  /** Saved, but without effect under the plan's current switches. */
  inactive: boolean
}

export function describePlanChanges(changes: PlanChanges, presentation: PlanPresentation = englishPlanPresentation): PlanChangeRow[] {
  return orderFields(Object.keys(changes)).map(field => ({
    field,
    label: fieldLabel(field, presentation),
    section: fieldSection(field),
    from: formatPlanValue(field, changes[field].from, presentation),
    to: formatPlanValue(field, changes[field].to, presentation),
  }))
}

export function describePlanValues(payload: PlanPayload, presentation: PlanPresentation = englishPlanPresentation): PlanValueRow[] {
  const values = payload as unknown as Record<string, unknown>
  return orderFields(PLAN_HISTORY_FIELDS.filter(field => field in values)).map(field => ({
    field,
    label: fieldLabel(field, presentation),
    section: fieldSection(field),
    value: formatPlanValue(field, values[field], presentation),
    inactive: isInactive(field, payload),
  }))
}

export function groupBySection<T extends { section: SectionId }>(rows: T[], presentation: PlanPresentation = englishPlanPresentation): { id: SectionId; title: string; rows: T[] }[] {
  return presentation.sections
    .map(section => ({ id: section.id, title: section.title, rows: rows.filter(row => row.section === section.id) }))
    .filter(group => group.rows.length > 0)
}

/** `720` → `30 days`. */
export function hoursAsDays(hours: number, presentation: PlanPresentation = englishPlanPresentation): string {
  return formatPlanUnit('days', Math.round((hours / 24) * 10) / 10, presentation)
}

export function hoursWithDays(hours: number, presentation: PlanPresentation = englishPlanPresentation): string {
  if (hours < 24) return formatPlanUnit('hours', hours, presentation)
  return presentation.t('hoursWithDays', { hours, days: Math.round((hours / 24) * 10) / 10 })
}

export function formatPlanTimestamp(value: string, presentation: PlanPresentation = englishPlanPresentation): string {
  return new Date(value).toLocaleString(presentation.locale, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/** Exact values as the editor inputs show them: no credit rounding. */
export function formatPlanValue(field: string, value: unknown, presentation: PlanPresentation = englishPlanPresentation): string {
  const numeric = Object.values(presentation.fields).find(definition => definition.key === field)
  if (numeric) return formatNumeric(numeric, value, presentation)
  if (value === null || value === undefined || value === '') return presentation.t('notSet')
  if (typeof value === 'boolean') return presentation.t(value ? 'on' : 'off')
  if (field === 'billing_intervals' && Array.isArray(value)) return value.length ? value.map(interval => formatInterval(interval, presentation)).join('; ') : presentation.t('none')
  if (Array.isArray(value)) return value.length ? value.join('; ') : presentation.t('none')
  if (typeof value === 'string') return formatText(field, value, presentation)
  if (typeof value === 'object' && Object.keys(value).length === 0) return presentation.t('none')
  return JSON.stringify(value)
}

const FORM_ORDER = Object.keys(DEFAULT_PLAN_FORM)
const SECTION_ORDER = englishPlanPresentation.sections.map(section => section.id)

function orderFields(fields: string[]): string[] {
  return [...fields].sort((a, b) =>
    SECTION_ORDER.indexOf(fieldSection(a)) - SECTION_ORDER.indexOf(fieldSection(b))
    || rank(a) - rank(b))
}

function rank(field: string): number {
  const index = FORM_ORDER.indexOf(field)
  return index === -1 ? FORM_ORDER.length : index
}

function fieldLabel(field: string, presentation: PlanPresentation): string {
  const numeric = Object.values(presentation.fields).find(definition => definition.key === field)
  return numeric?.label ?? presentation.labels[field as keyof typeof presentation.labels] ?? field
}

function formatNumeric(field: NumericField, value: unknown, presentation: PlanPresentation): string {
  if (value === null || value === undefined) return field.empty ?? presentation.t('notSet')
  if (typeof value !== 'number') return String(value)
  const amount = toEditorAmount(value, field)
  const text = field.unit ? formatPlanUnit(field.unit, Number(amount), presentation) : amount
  if (value === 0 && field.zero) return `${text} (${field.zero})`
  return field.unit === 'hours' ? hoursWithDays(value, presentation) : text
}

function formatInterval(value: unknown, presentation: PlanPresentation): string {
  const { interval, interval_count: count = 1, discount_percent: discount = 0 } = value as { interval?: string; interval_count?: number; discount_percent?: number }
  let period: string
  switch (interval) {
    case 'month': period = presentation.t('monthInterval', { count }); break
    case 'year': period = presentation.t('yearInterval', { count }); break
    case 'week': period = presentation.t('weekInterval', { count }); break
    case 'day': period = presentation.t('dayInterval', { count }); break
    default: period = presentation.t('periodInterval', { count })
  }
  return discount ? presentation.t('discount', { period, discount }) : period
}

function formatText(field: string, value: string, presentation: PlanPresentation): string {
  if (field === 'billing_channel') {
    switch (value) {
      case 'stripe': return 'Stripe'
      case 'shopify': return 'Shopify'
      default: return value
    }
  }
  if (field === 'status') {
    switch (value) {
      case 'published': return presentation.t('published')
      case 'internal': return presentation.t('internal')
      case 'archived': return presentation.t('archived')
      default: return value
    }
  }
  return value
}

function isInactive(field: string, plan: PlanPayload): boolean {
  switch (field) {
    case 'voice_trial_duration_hours':
    case 'voice_trial_budget_microusd':
      return plan.voice_included || !plan.voice_trial_enabled
    case 'campaigns_limit':
    case 'campaigns_trial_enabled':
      return !plan.campaigns_enabled
    case 'shopify_plan_handle':
      return plan.billing_channel !== BILLING_CHANNELS.SHOPIFY
    case 'plan_credits_expire':
      return plan.payment_to_balance_microusd === 0
    default:
      return false
  }
}
